/**
 * BubbleLevel — live TS16 tilt widget.
 *
 * Renders a circular spirit-level bullseye with a bubble whose offset
 * from centre reflects the instrument's cross/length inclination in
 * real time. The bullseye has two concentric tolerance rings so the
 * surveyor can spot-check the level state without doing arithmetic:
 *
 *   inner ring (green)  — within ±30 arc-seconds (~0.15 mrad, spec)
 *   outer ring (amber)  — within ±5 arc-minutes  (~1.45 mrad)
 *   outside outer ring  — bubble goes red, tripod needs work
 *
 * Inputs are RADIANS (matches the state.tilt_x / state.tilt_y that
 * the bridge poll fills from TMC_GetAngle5). Displays the numeric
 * values in ARC-SECONDS beneath the bullseye — 1 rad = 206264.8 arc-
 * seconds — which is the unit surveyors work with when discussing
 * levelling. Also shows a "Level OK" / "Level NOT OK" pill so a quick
 * glance answers the question without having to read the numbers.
 *
 * Sized for tablet use — the bullseye is 200 px so it's readable from
 * across the yard without leaning in. On smaller screens (mobile) it
 * scales down to 140 px via the `size` prop.
 */
import React from "react";

const RAD_TO_ARCSEC = 206264.806;

// Tolerance thresholds in RADIANS.
const OK_INNER_RAD = 30 / RAD_TO_ARCSEC;   // 30″
const OK_OUTER_RAD = 300 / RAD_TO_ARCSEC;  // 5′

export function BubbleLevel({ tiltX = 0, tiltY = 0, connected = false, size = 200 }) {
  // Radial magnitude drives the pass/fail colour and the "OK" banner.
  const mag = Math.hypot(tiltX || 0, tiltY || 0);
  const level =
    !connected ? "off" :
    mag < OK_INNER_RAD ? "ok" :
    mag < OK_OUTER_RAD ? "warn" : "fail";

  // Convert the tilt from radians to a pixel offset inside the
  // bullseye. Full-scale corresponds to 10 arc-minutes so a typical
  // "quite far off" setup pushes the bubble ~85 % out.
  const FULL_SCALE_RAD = 600 / RAD_TO_ARCSEC;  // 10′
  const R = size / 2 - 12;  // padding from bullseye edge
  const px = Math.max(-R, Math.min(R, (tiltX / FULL_SCALE_RAD) * R));
  const py = Math.max(-R, Math.min(R, (tiltY / FULL_SCALE_RAD) * R));

  const bubbleColour =
    level === "ok" ? "#8DC63F" :
    level === "warn" ? "#F5A623" :
    level === "fail" ? "#FF3B30" : "#4A5054";

  const innerR = (OK_INNER_RAD / FULL_SCALE_RAD) * R;
  const outerR = (OK_OUTER_RAD / FULL_SCALE_RAD) * R;

  return (
    <div className="flex flex-col items-center gap-2" data-testid="bubble-level">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="select-none">
        {/* Outer bullseye ring */}
        <circle
          cx={size / 2} cy={size / 2} r={R}
          fill="#0A0B0E" stroke="#2A2E39" strokeWidth="2"
        />
        {/* Amber tolerance ring — 5 arcmin */}
        <circle
          cx={size / 2} cy={size / 2} r={outerR}
          fill="none" stroke="#F5A623" strokeWidth="1" strokeDasharray="3 3" opacity="0.55"
        />
        {/* Green tolerance ring — 30 arcsec (spec limit) */}
        <circle
          cx={size / 2} cy={size / 2} r={innerR}
          fill="#8DC63F" fillOpacity="0.08"
          stroke="#8DC63F" strokeWidth="1"
        />
        {/* Cross hairs */}
        <line x1={size / 2} y1="6" x2={size / 2} y2={size - 6} stroke="#2A2E39" strokeWidth="1" />
        <line x1="6" y1={size / 2} x2={size - 6} y2={size / 2} stroke="#2A2E39" strokeWidth="1" />
        {/* Live bubble */}
        {connected && (
          <circle
            cx={size / 2 + px} cy={size / 2 + py} r="10"
            fill={bubbleColour}
            fillOpacity="0.75"
            stroke={bubbleColour}
            strokeWidth="2"
            style={{ transition: "cx 0.15s ease-out, cy 0.15s ease-out, fill 0.2s" }}
            data-testid="bubble-level-dot"
          />
        )}
        {/* Centre dot */}
        <circle cx={size / 2} cy={size / 2} r="1.5" fill="#5A6065" />
      </svg>

      {/* Numeric readout + status pill */}
      <div className="flex w-full items-center justify-between gap-3">
        <div className="flex flex-col text-[10px] uppercase tracking-widest text-[#8B939A]"
             style={{ fontFamily: "JetBrains Mono, monospace" }}>
          <span>X {connected ? `${(tiltX * RAD_TO_ARCSEC).toFixed(0)}″` : "—"}</span>
          <span>Y {connected ? `${(tiltY * RAD_TO_ARCSEC).toFixed(0)}″` : "—"}</span>
        </div>
        <div
          data-testid="bubble-level-status"
          className={`rounded-md border px-2 py-1 text-[10px] font-bold uppercase tracking-widest ${
            level === "ok"   ? "border-[#8DC63F] bg-[#8DC63F]/10 text-[#8DC63F]" :
            level === "warn" ? "border-[#F5A623] bg-[#F5A623]/10 text-[#F5A623]" :
            level === "fail" ? "border-[#FF3B30] bg-[#FF3B30]/10 text-[#FF3B30]" :
                               "border-[#2A2E39] bg-[#14161A] text-[#5A6065]"
          }`}
          style={{ fontFamily: "Chakra Petch, sans-serif" }}
        >
          {level === "ok"   ? "Level OK" :
           level === "warn" ? "Slightly off" :
           level === "fail" ? "Not level" :
                              "Offline"}
        </div>
      </div>
    </div>
  );
}

export default BubbleLevel;
